/**
 * ROD Review Dialog for Yotpo action.
 * Uses shared BaseModal and review components for consistency with campaigns.
 */
import { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';

import { api, useAPIContext } from '../../api/context';
import { useFPContext } from '../../utils/fingerprint';
import toast from '../../utils/toast';
import review from '../../components/review';
import BaseModal from '../BaseModal';
import styles from './RODReviewYotpo.module.scss';

const RODReviewYotpo = ({ item, onClose, onSubmit }) => {
  const { token } = useAPIContext();
  const { fp } = useFPContext();

  const config = item.config || {};
  const product = item.product;
  const previousData = item.previousData || {};
  const prefix = config.prefix || '';

  const [submitLoading, setSubmitLoading] = useState(false);

  const submit = async (values) => {
    setSubmitLoading(true);

    try {
      const fpResult = await fp.get();

      const response = await api(token).v1.products.rodSubmit.post(
        { rod_id: item.rod_id },
        {
          data_json: JSON.stringify(values),
          fingerprint: fpResult.visitorId,
        }
      );

      setSubmitLoading(false);

      if (response.status === 201) {
        if (onSubmit) onSubmit(response.body);
        if (onClose) onClose();
      } else if (response.status === 400) {
        toast.show(toast.KIND.ERROR, response.body?.error || response.body);
      }
    } catch (err) {
      setSubmitLoading(false);
      toast.show(toast.KIND.WARNING, err.message || err);
    }
  };

  const loading = submitLoading;

  return (
    <BaseModal title="Yotpo" onClose={onClose}>
      <div className={styles.root}>
        {/* Product header */}
        <div className={styles.product}>
          {product.images?.[0] && (
            <img src={product.images[0].image} alt="" />
          )}
          <div>
            <span>{product.brand_name}</span>
            <strong>{product.name}</strong>
          </div>
        </div>

        <Formik
          initialValues={{
            stars: previousData.stars || 0,
            title: previousData.title || '',
            text: previousData.text || prefix,
          }}
          validationSchema={yup.object({
            stars: yup.number()
              .min(1, 'Rating is required')
              .max(5, 'Invalid rating'),
            title: yup.string()
              .required('Title is required'),
            text: yup.string()
              .required('Review text is required'),
          })}
          validateOnChange={false}
          onSubmit={(values) => {
            if (loading) return;
            submit(values);
          }}
        >
          <Form>
            <review.Stars
              className={styles.stars}
              loading={loading}
              count={5}
            />

            <div className={styles.field}>
              <label htmlFor='title'>Title</label>
              <Field
                id='title'
                aria-label='title'
                name='title'
                type='text'
                disabled={loading}
              />
              <ErrorMessage
                className={styles.error}
                name='title'
                component='div'
              />
            </div>

            <review.Text
              className={styles.text}
              loading={loading}
              label={(
                <>
                  Review
                  {config.prefix && (
                    <span>
                      (Disclosure text added - DO NOT REMOVE THIS TEXT)
                    </span>
                  )}
                </>
              )}
              prefix={prefix}
              length={{
                min: config.min_length || 0,
                max: config.max_length || 0,
              }}
              nlp={{
                include: config.nlp_include,
                exclude: config.nlp_exclude,
                exclude_default: config.nlp_exclude_default,
              }}
            />

            <button
              type="submit"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Review'}
            </button>
          </Form>
        </Formik>
      </div>
    </BaseModal>
  );
};

export default RODReviewYotpo;
